import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, DateTimePicker, Input, Modal } from '@/components/ui'
import { UpdateVideoPayload, Video, VideoPayload } from '../../../common/models'
import { formatDateTime } from '../../../core/constants/times'
import {
  createVideo,
  deleteVideo,
  getVideos,
  updateVideo,
} from '../../../core/services/video'
import FormGroup from '../../../core/components/FormGroup'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'

export interface IVideoValues {
  name?: string
  previewImageLink?: string
  videoLink?: string
  publishedAt?: string
  published?: boolean
}

export interface MediaVideoCreateModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  setEditMode: (editMode: boolean) => void
  editMode: boolean
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
  setVideos: (videos: Video[]) => void
  video?: Video
}

export const MediaVideoCreateModal = (
  props: MediaVideoCreateModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    setEditMode,
    editMode,
    page,
    size,
    setTotalElemenets,
    setVideos,
    video,
  } = props
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const { editionId } = useParams()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    if (video) {
      setValue('name', video.name)
      setValue('videoLink', video.videoLink)
      setValue('published', video?.published)
      setValue('publishedAt', formatDateTime(video?.publishedAt))
    }

    return () => {
      reset()
    }
  }, [video, editMode])

  const schema = yup.object().shape({
    name: yup.string(),
    videoLink: yup.string(),
    published: yup.boolean(),
    publishedAt: yup.string(),
    previewImageLink: yup.string(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IVideoValues>({
    mode: 'onChange',
    resolver: yupResolver<IVideoValues>(schema),
    defaultValues: {
      videoLink: '',
      name: '',
      published: false,
      publishedAt: formatDateTime(new Date().valueOf()),
    },
  })

  const handleDelete = () => {
    // prettier-ignore
    (async () => {
      try {
        if (video) {
          const { status } = await deleteVideo(editionId as string, video?.id)
          const response = await getVideos(editionId as string, {
            page: page - 1,
            size,
          })
          setTotalElemenets(response.data.totalElements)
          setVideos(response.data.content)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const handleClose = () => {
    setShowCreateModal(false)
    setEditMode(false)
  }

  const submit = (values: IVideoValues) => {
    // prettier-ignore
    (async () => {
      try {
        if (video) {
          const data: UpdateVideoPayload = {
            videoLink: values.videoLink,
            published: new Date(values?.publishedAt || new Date()).toISOString() <= new Date().toISOString(),
            publishedAt: values.publishedAt
              ? new Date(values?.publishedAt).toISOString()
              : new Date().toISOString(),
            name: values.name,
          }

          const { status } = await updateVideo(video.id, editionId as string, data)
          const response = await getVideos(editionId as string, {
            page: page - 1,
            size,
          })
          setVideos(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Update Successful')
          }
        } else {
          const data: VideoPayload = {
            videoLink: values.videoLink,
            published: new Date(values?.publishedAt || new Date()).toISOString() <= new Date().toISOString(),
            publishedAt: values.publishedAt
              ? new Date(values?.publishedAt).toISOString()
              : new Date().toISOString(),
            name: values.name,
          }

          const { status } = await createVideo(editionId as string, data)
          const response = await getVideos(editionId as string, {
            page: page - 1,
            size,
          })
          setTotalElemenets(response.data.totalElements)
          setVideos(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Create Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="play"
              title="Video Gallery"
              subtitle={editMode ? video?.name : 'Add New Video'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editMode ? (
              <Button
                onClick={() => setDeleteConfirmationModal(true)}
                variant="secondary"
                className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
              >
                Delete
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="media-video-form"
                type="submit"
                variant="primary"
                disabled={Object.keys(dirtyFields).length === 0}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="media-video-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
              className="bg-monochrome-5-50"
            />
          </FormGroup>
          <FormGroup label="Youtube Link" error={errors?.videoLink?.message}>
            <Input
              isInvalid={!!errors?.videoLink?.message}
              {...register('videoLink')}
              type="text"
              placeholder="Enter Youtube video URL"
              className="bg-monochrome-5-50"
            />
          </FormGroup>
          <Controller
            name="publishedAt"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                label="Publish Date & Time"
                hasError={!!errors?.publishedAt?.message}
                value={field.value ? new Date(field.value) : null}
                onChange={(date) =>
                  field.onChange(date ? formatDateTime(date.valueOf()) : '')
                }
                placeholder="Enter Publish Date and Time"
              />
            )}
          />
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Video'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
